/**
 * Normalizes mermaid.render()'s raw SVG string so every later consumer —
 * the live preview, export.ts's own DOMParser pass, Copy-as — can rely on
 * strict XML parsing succeeding and on the SVG's own size being
 * self-contained, before it ever reaches the DOM.
 *
 * Two independent problems, fixed together because the second is unreachable
 * without the first:
 *
 * 1. **Missing `xlink` namespace.** Some diagram types (confirmed: C4
 *    context/container, whose Person/System icons are `<image
 *    xlink:href="...">`) never declare `xmlns:xlink` on the root — SPEC.md
 *    §6 gotcha 2's own "Mermaid does not always include them," except an
 *    undeclared prefix isn't just an interop nicety for a standalone file,
 *    it's a *fatal* error under `DOMParser`'s strict `image/svg+xml` mode.
 *    That parse is exactly what export.ts's `fixSvgMarkup` already depends
 *    on to add this very namespace (gotcha 2) — a chicken-and-egg bug that
 *    made "Save SVG" throw "Could not parse SVG markup" and silently
 *    produce no download at all for C4 diagrams, well before this module
 *    existed. Declaring the namespace with a plain string edit, ahead of
 *    any parse, breaks the cycle.
 *
 * 2. **Ambiguous intrinsic size.** `width="100%"` with no `height`
 *    attribute at all means the live preview's box size depends on its own
 *    absolutely-positioned parent's resolved width — a circular,
 *    timing-sensitive layout. Preview.tsx's auto-fit reads that box's
 *    offsetWidth/Height synchronously the instant the SVG is inserted (a
 *    `useLayoutEffect`); if that circular sizing hasn't settled on that
 *    exact frame, the fit locks onto a wrong content size — the observed
 *    bug: a diagram rendered successfully ("Drawn", no error) but shown as
 *    a blank, badly-zoomed sheet. Setting explicit pixel width/height from
 *    viewBox (mirroring fixSvgMarkup's own gotcha-3 handling, applied here
 *    to the live copy too) removes the circularity outright.
 */
import { parseViewBox } from './export';

const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

/** A plain string edit, not a DOM operation — this exists specifically to run *before* any XML-strict parse can be attempted at all. */
function ensureXlinkNamespaceDeclared(svgMarkup: string): string {
  if (!svgMarkup.startsWith('<svg') || svgMarkup.includes('xmlns:xlink')) return svgMarkup;
  return svgMarkup.replace('<svg', `<svg xmlns:xlink="${XLINK_NAMESPACE}"`);
}

/**
 * Returns the namespace-safe markup unchanged (rather than the original
 * input) whenever the dimension fix specifically can't apply — a missing
 * viewBox, say — so gotcha 2's fix always survives downstream even when
 * gotcha 3's can't. Only falls back to the raw, unfixed input if parsing
 * fails outright even after the namespace fix; mermaid.render() always
 * emits a well-formed, viewBox-carrying SVG once that's declared (export.ts's
 * fixSvgMarkup makes the same assumption), so that path is defensive, not
 * expected.
 */
export function withExplicitDimensions(svgMarkup: string): string {
  const namespaced = ensureXlinkNamespaceDeclared(svgMarkup);
  const doc = new DOMParser().parseFromString(namespaced, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return namespaced;

  const svg = doc.documentElement;
  const viewBoxAttr = svg.getAttribute('viewBox');
  const viewBox = viewBoxAttr === null ? null : parseViewBox(viewBoxAttr);
  if (viewBox === null) return namespaced;

  svg.setAttribute('width', String(viewBox.width));
  svg.setAttribute('height', String(viewBox.height));
  return new XMLSerializer().serializeToString(svg);
}
